import React, { useEffect, useRef, useState } from 'react';
import Lenis from '@studio-freight/lenis';
import './merlionTheme.css';
import OpenMapsLink from "./OpenMapsLink";

import heroImg from './merliongymnasium/merlion-gymnasium.webp';
import g1Img from './merliongymnasium/merlion-gymnasium-g1.webp';
import g2Img from './merliongymnasium/merlion-gymnasium-g2.webp';
import g3Img from './merliongymnasium/merlion-gymnasium-g3.webp';
import g4Img from './merliongymnasium/merlion-gymnasium-g4.webp';
import g5Img from './merliongymnasium/merlion-gymnasium-g5.webp';
import appMockup from './merliongymnasium/app-mockup.png';
import ongroundLogo from '../assets/branding/onground-powered-by-light-bg.png';
import OngroundStoreIcon from '../OngroundStoreIcon';
import {
  resolveOngroundAppLink,
  resolveVenueSocialLink,
  resolveThemeArray,
  resolveThemeCopy,
  resolveThemeLogo,
  resolveThemeMedia,
  resolveThemeObject,
} from '../publicWebsiteContent';
import { resolveThemePaletteStyle } from '../themePalette';
import { isWebsiteSectionVisible } from '../themeDefinitions';
import usePublicContactEnquiry from '../usePublicContactEnquiry';
import PublicContactEnquiryStatus, { PublicContactHoneypot, PublicContactPhoneField } from '../PublicContactEnquiryStatus';

// ── Exact original fallbacks ─────────────────────────────────────────────────
const STATIC_COURTS = [
  {
    id: 'half-a',
    sport: 'basketball',
    sportLabel: 'Basketball',
    sportIcon: '🏀',
    name: 'Half Court A',
    price: 'NPR 1,500/hr',
    img: g1Img,
    layout: 'quarter',
    desc: 'Well-lit half basketball court ideal for 3v3 games, skill drills, and casual pickup sessions.',
    features: ['Half Court', 'Maple Floor', 'LED Lights', 'Backboard & Rim'],
  },
  {
    id: 'full-court',
    sport: 'basketball',
    sportLabel: 'Basketball',
    sportIcon: '🏀',
    name: 'Full Court Basketball',
    price: 'NPR 2,000/hr',
    img: g2Img,
    layout: 'featured',
    desc: 'Our flagship full-length basketball court with premium maple hardwood, official multi-line markings, and stadium-grade LED floodlighting. Perfect for 5v5 competitive matches and training.',
    features: ['Full Court', 'Maple Hardwood', 'Stadium Lighting', 'Pro Backboards', 'Multi-Sport Lines'],
  },
  {
    id: 'volleyball',
    sport: 'volleyball',
    sportLabel: 'Volleyball',
    sportIcon: '🏐',
    name: 'Volleyball Court',
    price: 'NPR 2,000/hr',
    img: g3Img,
    layout: 'standard',
    desc: 'Dedicated indoor volleyball court with regulation net setup and a sprung hardwood surface that\'s gentle on joints during intense play.',
    features: ['Full Volleyball', 'Regulation Net', 'Maple Floor', 'Team Seating'],
  },
  {
    id: 'badminton',
    sport: 'badminton',
    sportLabel: 'Badminton',
    sportIcon: '🏸',
    name: 'Badminton Court',
    price: 'NPR 1,500/hr',
    img: g4Img,
    layout: 'quarter',
    desc: 'Marked badminton court with optimal ceiling height and clear floor lines for singles and doubles play.',
    features: ['Singles & Doubles', 'Official Lines', 'LED Lighting', 'Net Included'],
  },
  {
    id: 'half-b',
    sport: 'basketball',
    sportLabel: 'Basketball',
    sportIcon: '🏀',
    name: 'Half Court B',
    price: 'NPR 1,500/hr',
    img: g5Img,
    layout: 'quarter',
    desc: 'Second half-court setup perfect for simultaneous independent team training and separate shooting practice.',
    features: ['Half Court', 'Maple Floor', 'LED Lights', 'Backboard & Rim'],
  },
];

const STATIC_STATS = [
  { n: 5, suffix: '', label: 'Indoor Courts' },
  { n: 16, suffix: ' hrs', label: 'Open Daily' },
  { n: 3, suffix: '', label: 'Sports Offered' },
  { n: 1500, suffix: '/hr', label: 'Starting (NPR)' },
];

const STATIC_AMENITIES = [
  { icon: '🅿️', label: 'Parking' },
  { icon: '🚿', label: 'Washroom' },
  { icon: '👕', label: 'Changing Room' },
  { icon: '💡', label: 'Floodlights' },
  { icon: '🏆', label: 'Coaching' },
  { icon: '🎒', label: 'Equipment Rental' },
];

const STATIC_TESTIMONIALS = [
  {
    text: 'The best indoor sports facility in Lazimpat. The maple hardwood courts are in excellent condition and the lighting is perfect for evening games. We use it weekly for our basketball league.',
    name: 'Bibek Tamang',
    role: 'Basketball League Player',
    rating: 5,
  },
  {
    text: 'Clean, well-maintained courts and very professional staff. We organize our school volleyball tournaments here every semester. Highly recommended for group bookings.',
    name: 'Sunita Karki',
    role: 'School Sports Coach',
    rating: 5,
  },
  {
    text: 'One of the few places in Kathmandu with proper badminton court dimensions. The floor is great and the app booking through ONGROUND is super convenient.',
    name: 'Rajan Thapa',
    role: 'Regular Badminton Player',
    rating: 5,
  },
];

const STATIC_FAQS = [
  {
    q: 'How can I book a court at Merlion Gymnasium?',
    a: 'You can book instantly via the ONGROUND app 24/7 or call us at +977 9843566401. Walk-ins are welcome subject to availability.',
  },
  {
    q: 'Which sports courts are available?',
    a: 'We offer 5 courts covering 3 sports: Full Court Basketball, Half Court A & B (Basketball), Volleyball, and Badminton — all on premium maple hardwood flooring.',
  },
  {
    q: 'What are the opening hours?',
    a: 'Merlion Gymnasium is open daily from 6:00 AM to 10:00 PM, including weekends and public holidays.',
  },
  {
    q: 'Is coaching or training available?',
    a: 'Yes! We have experienced coaches available for basketball, volleyball, and badminton. Contact us to schedule private or group training sessions.',
  },
  {
    q: 'What amenities are provided?',
    a: 'The facility includes free parking, washrooms, changing rooms, LED stadium lighting, and equipment rental. We maintain high standards of cleanliness throughout.',
  },
  {
    q: 'Can we book for tournaments or events?',
    a: 'Absolutely. We offer full venue hire for tournaments, corporate sports days, and school events. Contact us directly for event-specific pricing and logistics.',
  },
];

const STATIC_MARQUEE = [
  'BASKETBALL',
  'VOLLEYBALL',
  'BADMINTON',
  'INDOOR SPORTS',
  'PREMIUM COURTS',
  'MAPLE HARDWOOD',
];

const MERLION_THEME_ID = 'merlion-gymnasium';
const STATIC_MAP_EMBED_URL =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3531.9!2d85.3178!3d27.7229!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjfCsDQzJzIyLjQiTiA4NcKwMTknMDQuMSJF!5e0!3m2!1sen!2snp!4v1';

const FACILITY_PRESENTATION = Object.freeze({
  parking: { icon: '🅿️', label: 'Parking' },
  washroom: { icon: '🚿', label: 'Washroom' },
  changing_room: { icon: '👕', label: 'Changing Room' },
  lights: { icon: '💡', label: 'Floodlights' },
  cafeteria: { icon: '☕', label: 'Cafeteria' },
  equipment_rental: { icon: '🎒', label: 'Equipment Rental' },
});

const SPORT_ICON_BY_KEY = Object.freeze({
  basketball: '🏀',
  volleyball: '🏐',
  badminton: '🏸',
  football: '⚽',
  futsal: '⚽',
  pickleball: '🏓',
  cricket: '🏏',
  tennis: '🎾',
});

const cleanText = (value) => String(value == null ? '' : value).trim();

const formatLabel = (value) =>
  cleanText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeKey = (value) =>
  cleanText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const formatTime = (value, fallback) => {
  const input = cleanText(value);

  if (!/^\d{1,2}:\d{2}$/.test(input)) {
    return input || fallback;
  }

  const [hourText, minute] = input.split(':');
  const hour = Number(hourText);

  if (!Number.isFinite(hour)) {
    return fallback;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const getOpenHoursDuration = (openTime, closeTime) => {
  const parseMinutes = (value) => {
    const match = cleanText(value).match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return hour * 60 + minute;
  };

  const start = parseMinutes(openTime);
  const end = parseMinutes(closeTime);

  if (start == null || end == null) {
    return null;
  }

  const durationMinutes =
    end > start
      ? end - start
      : end === start
        ? 24 * 60
        : end + 24 * 60 - start;

  return Math.round(durationMinutes / 60);
};

const getVenuePhone = (venue) =>
  cleanText(venue?.contact?.e164) ||
  cleanText(venue?.contact?.nationalNumber) ||
  '';

const getVenueAddress = (venue) => {
  const address = cleanText(venue?.location?.address);
  if (address) return address;

  return [
    cleanText(venue?.location?.city),
    cleanText(venue?.location?.country || venue?.country),
  ]
    .filter(Boolean)
    .join(', ');
};

const getShortLocation = (venue) => {
  const address = cleanText(venue?.location?.address);
  const city = cleanText(venue?.location?.city);
  const country = cleanText(venue?.location?.country || venue?.country);
  const locality = cleanText(address.split(',')[0]);
  const parts = [];

  [locality, city, country].forEach((part) => {
    if (
      part &&
      !parts.some(
        (existing) => existing.toLowerCase() === part.toLowerCase(),
      )
    ) {
      parts.push(part);
    }
  });

  return parts.slice(0, 2).join(' · ');
};

const getSafeExternalUrl = (value, fallback = '#') => {
  const input = cleanText(value);

  if (!input || input === '#') {
    return fallback;
  }

  try {
    const parsed = new URL(input);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return fallback;
    }

    return parsed.toString();
  } catch {
    return fallback;
  }
};

const buildMapEmbedUrl = (venue, configuredUrl) => {
  const rawLatitude = venue?.location?.latitude;
  const rawLongitude = venue?.location?.longitude;
  const latitude = rawLatitude === null || rawLatitude === undefined || rawLatitude === ''
    ? Number.NaN
    : Number(rawLatitude);
  const longitude = rawLongitude === null || rawLongitude === undefined || rawLongitude === ''
    ? Number.NaN
    : Number(rawLongitude);
  const hasValidCoordinates =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  if (hasValidCoordinates) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}&z=15&output=embed`;
  }

  const safeConfiguredUrl = getSafeExternalUrl(configuredUrl, '');
  if (safeConfiguredUrl) {
    return safeConfiguredUrl;
  }

  const address = getVenueAddress(venue);
  return address
    ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`
    : STATIC_MAP_EMBED_URL;
};

const getGroundImage = (ground) =>
  cleanText(ground?.mainImageUrl || ground?.imageUrl);

const getGroundSportLabel = (ground) =>
  formatLabel(
    ground?.sportName ||
      ground?.sport?.name ||
      ground?.type,
  );

const formatGroundPrice = (ground, venue) => {
  const rawAmount =
    ground?.basePrice ??
    ground?.price ??
    ground?.pricing?.basePrice ??
    venue?.pricing?.basePrice;

  if (rawAmount === undefined || rawAmount === null || rawAmount === '') {
    return '';
  }

  const amount = Number(rawAmount);

  if (!Number.isFinite(amount) || amount < 0) {
    return '';
  }

  const currency = cleanText(
    ground?.currency ||
      ground?.pricing?.currency ||
      venue?.pricing?.currency,
  ).toUpperCase();

  const unit = cleanText(ground?.unit).toLowerCase();
  const unitSuffix = unit === 'slot' ? '/slot' : '/hr';

  return `${currency || 'NPR'} ${amount.toLocaleString('en-US')}${unitSuffix}`;
};

const normalizeRating = (value) => {
  const rating = Math.round(Number(value));

  return Number.isFinite(rating)
    ? Math.min(5, Math.max(1, rating))
    : 5;
};

const splitVenueName = (venueName) => {
  const parts = cleanText(venueName).split(/\s+/).filter(Boolean);

  return {
    primary: parts[0] || 'Merlion',
    secondary:
      parts.length > 1
        ? parts.slice(1).join(' ')
        : parts.length === 0
          ? 'Gymnasium'
          : '',
  };
};

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useScrollAnimation(disabled = false) {
  useEffect(() => {
    if (disabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('mg-in-view');
          }
        }),
      { threshold: 0.1 },
    );

    document
      .querySelectorAll('[data-animate]')
      .forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [disabled]);
}

function useCounter(target, inView) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;

    let start = 0;
    let animationFrameId = 0;
    const duration = 1800;

    const step = (timestamp) => {
      if (!start) start = timestamp;

      const progress = Math.min((timestamp - start) / duration, 1);
      setCount(Math.floor(progress * target));

      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
    };

    animationFrameId = window.requestAnimationFrame(step);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [inView, target]);

  return count;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function MerlionTheme({
  venue = {},
  grounds = [],
  website = {},
  primaryColor = '#CC2936',
  accentColor = '#1A1A1A',
  heroImage: commonHeroImage = '',
  openBooking = () => {},
  previewMode = false,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  const themePaletteStyle = resolveThemePaletteStyle(
    MERLION_THEME_ID,
    primaryColor,
    accentColor,
  );

  const t = (key, fallback, apiValue = '') =>
    resolveThemeCopy(
      website,
      MERLION_THEME_ID,
      key,
      fallback,
      apiValue,
    );

  const m = (key, fallback) =>
    resolveThemeMedia(
      website,
      MERLION_THEME_ID,
      key,
      fallback,
    );

  const {
    values: contactForm,
    handleChange: handleContactChange,
    submit: handleContactSubmit,
    submitting: contactSubmitting,
    feedback: contactFeedback,
  } = usePublicContactEnquiry({
    venue,
    previewMode,
    successMessage: t('formSuccessMessage', "Message sent! We'll get back to you shortly."),
  });

  const social = resolveThemeObject(
    website,
    MERLION_THEME_ID,
    'social',
    {},
  );

  const venueName = cleanText(venue?.name) || 'Merlion Gymnasium';
  const brand = splitVenueName(venueName);
  const venueAddress = getVenueAddress(venue) || 'Lazimpat, Kathmandu';
  const shortLocation = getShortLocation(venue) || 'Lazimpat · Kathmandu';
  const venuePhone = getVenuePhone(venue) || '+977 9843566401';

  const rawOpenTime = cleanText(venue?.policies?.openTime) || '06:00';
  const rawCloseTime = cleanText(venue?.policies?.closeTime) || '22:00';
  const openingTime = formatTime(rawOpenTime, '6:00 AM');
  const closingTime = formatTime(rawCloseTime, '10:00 PM');
  const displayHours = `${openingTime} – ${closingTime}`;
  const openHoursDuration =
    getOpenHoursDuration(rawOpenTime, rawCloseTime) || 16;

  const venueGrounds = Array.isArray(grounds) ? grounds : [];
  const gallery = Array.isArray(venue?.media?.gallery)
    ? venue.media.gallery.filter(Boolean)
    : [];

  const featuredGroundIndex = venueGrounds.length === 1 ? 0 : 1;

  const courtCards = (venueGrounds.length > 0
    ? venueGrounds.map((ground, index) => {
        const fallback = STATIC_COURTS[index % STATIC_COURTS.length];
        const sportLabel = getGroundSportLabel(ground) || fallback.sportLabel;
        const sportKey = normalizeKey(sportLabel);
        const fallbackLayout =
          fallback.layout === 'featured'
            ? 'standard'
            : fallback.layout;

        return {
          id: cleanText(ground?._id || ground?.id) || `ground-${index + 1}`,
          ground,
          sport: sportKey || fallback.sport,
          sportLabel,
          sportIcon: SPORT_ICON_BY_KEY[sportKey] || fallback.sportIcon,
          name: cleanText(ground?.name) || fallback.name,
          price: formatGroundPrice(ground, venue) || fallback.price,
          img:
            getGroundImage(ground) ||
            gallery[index] ||
            fallback.img,
          layout:
            index === featuredGroundIndex
              ? 'featured'
              : fallbackLayout,
          desc: cleanText(ground?.description) || fallback.desc,
          features: fallback.features,
        };
      })
    : STATIC_COURTS.map((court) => ({ ...court, ground: null })))
    .filter((court) => court.name && court.img);

  const featuredCourts = courtCards.filter(
    (court) => court.layout === 'featured',
  );
  const bentoCourts = courtCards.filter(
    (court) => court.layout !== 'featured',
  );

  const uniqueSportLabels = Array.from(
    new Set(
      courtCards
        .map((court) => cleanText(court.sportLabel))
        .filter(Boolean),
    ),
  );

  const groundCount = venueGrounds.length || STATIC_STATS[0].n;
  const sportCount = uniqueSportLabels.length || STATIC_STATS[2].n;

  const minimumGroundPrice = venueGrounds
    .map((ground) =>
      Number(
        ground?.basePrice ??
          ground?.price ??
          ground?.pricing?.basePrice,
      ),
    )
    .filter((amount) => Number.isFinite(amount) && amount >= 0)
    .sort((left, right) => left - right)[0];

  const venueBasePrice = Number(
    minimumGroundPrice ?? venue?.pricing?.basePrice,
  );

  const startingPrice =
    Number.isFinite(venueBasePrice) && venueBasePrice >= 0
      ? venueBasePrice
      : STATIC_STATS[3].n;

  const startingCurrency =
    cleanText(
      venueGrounds.find((ground) => {
        const amount = Number(
          ground?.basePrice ??
            ground?.price ??
            ground?.pricing?.basePrice,
        );

        return amount === minimumGroundPrice;
      })?.currency || venue?.pricing?.currency,
    ).toUpperCase() || 'NPR';

  const stats = [
    {
      n: groundCount,
      suffix: '',
      label: t('statsCourtsLabel', 'Indoor Courts'),
    },
    {
      n: openHoursDuration,
      suffix: ' hrs',
      label: t('statsHoursLabel', 'Open Daily'),
    },
    {
      n: sportCount,
      suffix: '',
      label: t('statsSportsLabel', 'Sports Offered'),
    },
    {
      n: startingPrice,
      suffix: '/hr',
      label: t(
        'statsStartingLabel',
        'Starting (NPR)',
        `Starting (${startingCurrency})`,
      ),
    },
  ];

  const apiAmenities = Array.isArray(venue?.facilities)
    ? venue.facilities
        .map((facility) => {
          const key = normalizeKey(facility);
          if (!key) return null;

          return (
            FACILITY_PRESENTATION[key] || {
              icon: '✓',
              label: formatLabel(facility),
            }
          );
        })
        .filter(Boolean)
    : [];

  const amenities = apiAmenities.length > 0
    ? apiAmenities
    : STATIC_AMENITIES;

  const testimonials = resolveThemeArray(
    website,
    MERLION_THEME_ID,
    'testimonials',
    STATIC_TESTIMONIALS,
  ).map((testimonial, index) => {
    const fallback =
      STATIC_TESTIMONIALS[index % STATIC_TESTIMONIALS.length];

    return {
      text: cleanText(testimonial?.text) || fallback.text,
      name: cleanText(testimonial?.name) || fallback.name,
      role:
        cleanText(testimonial?.role || testimonial?.sport) ||
        fallback.role,
      rating: normalizeRating(testimonial?.rating ?? fallback.rating),
    };
  });

  const apiFaqs = STATIC_FAQS.map((faq, index) => {
    if (index === 0) {
      return {
        ...faq,
        q: `How can I book a court at ${venueName}?`,
        a: getVenuePhone(venue)
          ? `You can book instantly via the ONGROUND app 24/7 or call us at ${venuePhone}. Walk-ins are welcome subject to availability.`
          : faq.a,
      };
    }

    if (index === 1 && venueGrounds.length > 0) {
      const courtNames = courtCards
        .map((court) => court.name)
        .filter(Boolean)
        .join(', ');
      const sportsText = uniqueSportLabels.join(', ');

      return {
        ...faq,
        a: `We offer ${groundCount} courts covering ${sportCount} sports${sportsText ? `: ${sportsText}` : ''}${courtNames ? `. Available courts include ${courtNames}.` : '.'}`,
      };
    }

    if (index === 2) {
      return {
        ...faq,
        a: `${venueName} is open daily from ${openingTime} to ${closingTime}, including weekends and public holidays.`,
      };
    }

    if (index === 4 && apiAmenities.length > 0) {
      return {
        ...faq,
        a: `The facility includes ${apiAmenities
          .map((amenity) => amenity.label.toLowerCase())
          .join(', ')}. We maintain high standards of cleanliness throughout.`,
      };
    }

    return faq;
  });

  const faqs = resolveThemeArray(
    website,
    MERLION_THEME_ID,
    'faqs',
    STATIC_FAQS,
    apiFaqs,
  ).map((faq, index) => {
    const fallback = STATIC_FAQS[index % STATIC_FAQS.length];

    return {
      q: cleanText(faq?.q || faq?.question) || fallback.q,
      a: cleanText(faq?.a || faq?.answer) || fallback.a,
    };
  });

  const apiMarqueeWords = uniqueSportLabels.length > 0
    ? [
        ...uniqueSportLabels.map((sport) => sport.toUpperCase()),
        'INDOOR SPORTS',
        'PREMIUM COURTS',
        'MAPLE HARDWOOD',
      ]
    : [];

  const marqueeWords = resolveThemeArray(
    website,
    MERLION_THEME_ID,
    'marqueeWords',
    STATIC_MARQUEE.map((word) => ({ word })),
    apiMarqueeWords.map((word) => ({ word })),
  )
    .map((item) =>
      cleanText(typeof item === 'string' ? item : item?.word),
    )
    .filter(Boolean);

  const navigation = [
    { id: 'courts', sectionKey: 'grounds', label: t('navCourts', 'Courts') },
    { id: 'about', sectionKey: 'about', label: t('navAbout', 'About') },
    { id: 'faq', sectionKey: 'faqs', label: t('navFaq', 'FAQ') },
    { id: 'contact', sectionKey: 'contact', label: t('navContact', 'Contact') },
  ].filter(({ sectionKey }) =>
    isWebsiteSectionVisible(website, sectionKey),
  );

  const resolvedHeroImage = m(
    'heroImageUrl',
    cleanText(commonHeroImage) ||
      cleanText(venue?.media?.bannerImage) ||
      gallery[0] ||
      heroImg,
  );

  const featuredGround =
    featuredCourts[0]?.ground || venueGrounds[0] || null;

  const resolvedAboutImage = m(
    'aboutImageUrl',
    getGroundImage(featuredGround) ||
      gallery[1] ||
      gallery[0] ||
      g2Img,
  );

  const resolvedVenueLogo = resolveThemeLogo(
    website,
    MERLION_THEME_ID,
    '',
  );
  const resolvedAppImage = m('appImageUrl', appMockup);
  const resolvedPoweredLogo = m('poweredLogoUrl', ongroundLogo);

  const heroBadgeLocation =
    cleanText(venue?.location?.address).split(',')[0] ||
    cleanText(venue?.location?.city);

  const heroBadgeApi = heroBadgeLocation
    ? `Indoor Multi-Sports Complex · ${heroBadgeLocation}`
    : '';

  const heroTitleLineOne = cleanText(t('heroTitleLineOne', 'WHERE EVERY'));
  const heroTitleLineOneParts = heroTitleLineOne.match(/^(.*\S)\s+(EVERY)$/i);

  const resolvedHeroDescription = t(
    'heroDescription',
    "Kathmandu's premier indoor multi-sport complex. Premium maple hardwood courts for basketball, volleyball, and badminton — all under one roof in Lazimpat.",
    cleanText(venue?.shortDescription || venue?.fullDescription),
  );

  const resolvedAboutDescription = t(
    'aboutDescription',
    'Merlion Gymnasium is a modern indoor multi-sports complex in Kathmandu offering premium facilities for basketball, badminton, volleyball, and more — ideal for both casual play and competitive training. Our courts feature beautiful maple hardwood flooring with official multi-sport line markings, professional backstops, and stadium-grade LED floodlighting, ensuring an elite sporting experience every visit.',
    cleanText(venue?.fullDescription || venue?.shortDescription),
  );

  const resolvedFooterTagline = t(
    'footerTagline',
    "Kathmandu's premier indoor multi-sport complex. 5 premium maple courts. 3 sports. One venue.",
    cleanText(venue?.shortDescription),
  );

  const appStoreUrl = resolveOngroundAppLink(
    social?.appStoreUrl,
    'appStore',
  );
  const googlePlayUrl = resolveOngroundAppLink(
    social?.googlePlayUrl,
    'googlePlay',
  );
  const mapEmbedUrl = buildMapEmbedUrl(venue, social?.mapEmbedUrl);

  const footerSocialItems = [
    {
      key: 'instagram',
      label: t('instagramLabel', 'Instagram'),
      href: resolveVenueSocialLink(
        social?.instagramUrl,
        'instagram',
      ),
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
      ),
    },
    {
      key: 'facebook',
      label: t('facebookLabel', 'Facebook'),
      href: resolveVenueSocialLink(
        social?.facebookUrl,
        'facebook',
      ),
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
      ),
    },
    {
      key: 'tiktok',
      label: t('tiktokLabel', 'TikTok'),
      href: resolveVenueSocialLink(
        social?.tiktokUrl,
        'tiktok',
      ),
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
      ),
    },
    {
      key: 'whatsapp',
      label: t('whatsappLabel', 'WhatsApp'),
      href: resolveVenueSocialLink(
        social?.whatsappUrl,
        'whatsapp',
      ),
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
      ),
    },
  ].filter((item) => item.href);

  const canBook =
    website?.booking?.enabled !== false &&
    typeof openBooking === 'function';

  const openGroundBooking = (ground = null) => {
    if (previewMode) return;

    if (canBook) {
      openBooking(ground);
      return;
    }

    scrollTo('contact');
  };

  useScrollAnimation(previewMode);

  useEffect(() => {
    if (previewMode) return undefined;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (time) =>
        Math.min(1, 1.001 - Math.pow(2, -10 * time)),
    });

    let animationFrameId = 0;

    const raf = (time) => {
      lenis.raf(time);
      animationFrameId = window.requestAnimationFrame(raf);
    };

    animationFrameId = window.requestAnimationFrame(raf);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      lenis.destroy();
    };
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) return undefined;

    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) {
      setStatsInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsInView(true);
      },
      { threshold: 0.3 },
    );

    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, [previewMode]);

  const scrollTo = (id) => {
    if (previewMode) return;

    const target = document.getElementById(id);

    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div
      className={`mg-root ${previewMode ? 'mg-theme-preview' : ''}`}
      style={themePaletteStyle}
    >
      {/* ── NAVBAR ── */}
      <header className={`mg-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="mg-container mg-nav-inner">
          <button className="mg-logo" onClick={() => scrollTo('hero')}>
            {resolvedVenueLogo ? (
              <img
                className="mg-logo-image"
                src={resolvedVenueLogo}
                alt={`${venueName} logo`}
              />
            ) : (
              <div className="mg-logo-icon">{brand.primary.charAt(0).toUpperCase()}</div>
            )}
            <div>
              <div className="mg-logo-name"><span>{brand.primary}</span>{brand.secondary ? ` ${brand.secondary}` : ''}</div>
              <span className="mg-logo-sub">{shortLocation}</span>
            </div>
          </button>
          <nav className="mg-nav-links">
            {navigation.map((link) => (
              <button key={link.id} className="mg-nav-link" onClick={() => scrollTo(link.id)}>
                {link.label}
              </button>
            ))}
          </nav>
          <button className="mg-btn-primary mg-btn-sm" onClick={() => openGroundBooking()}>
            {t('navBookLabel', 'Book Now')}
          </button>
        </div>
      </header>

      {/* ── HERO ── */}
      {isWebsiteSectionVisible(website, "hero") && (
      <section id="hero" className="mg-hero">
        <div className="mg-container mg-hero-grid">
          <div className="mg-hero-content">
            <div className="mg-hero-badge" data-animate="fade-down">
              <span className="mg-hero-badge-dot" />
              {t('heroBadge', 'Indoor Multi-Sports Complex · Lazimpat', heroBadgeApi)}
            </div>
            <h1 className="mg-hero-title" data-animate="fade-up">
              {heroTitleLineOneParts ? (
                <>
                  {heroTitleLineOneParts[1]}{' '}
                  <span className="mg-hero-title-every">{heroTitleLineOneParts[2]}</span>
                </>
              ) : heroTitleLineOne}<br />
              {t('heroTitleLineTwo', 'GAME')}<br />
              <span className="mg-text-crimson">{t('heroTitleHighlight', 'BEGINS.')}</span>
            </h1>
            <p className="mg-hero-sub" data-animate="fade-up" style={{ '--delay': '0.1s' }}>
              {resolvedHeroDescription}
            </p>
            <div className="mg-hero-actions" data-animate="fade-up" style={{ '--delay': '0.2s' }}>
              {isWebsiteSectionVisible(website, "grounds") && (
                <button className="mg-btn-primary mg-btn-lg" onClick={() => scrollTo('courts')}>
                  {t('heroViewCourtsLabel', 'View All Courts')}
                </button>
              )}
              <button className="mg-btn-outline mg-btn-lg" onClick={() => openGroundBooking()}>
                {t('heroBookLabel', 'Book a Session')}
              </button>
            </div>
          </div>

          <div className="mg-hero-img-wrap" data-animate="fade-left" style={{ '--delay': '0.15s' }}>
            <div className="mg-hero-img-main">
              <img src={resolvedHeroImage} alt={`${venueName} Premium Court`} />
            </div>
            <div className="mg-hero-stat-pill">
              <div className="mg-hero-stat-item">
                <div className="mg-hero-stat-num">{groundCount}</div>
                <div className="mg-hero-stat-label">{t('heroCourtsStatLabel', 'Courts')}</div>
              </div>
              <div className="mg-hero-stat-divider" />
              <div className="mg-hero-stat-item">
                <div className="mg-hero-stat-num">{sportCount}</div>
                <div className="mg-hero-stat-label">{t('heroSportsStatLabel', 'Sports')}</div>
              </div>
              <div className="mg-hero-stat-divider" />
              <div className="mg-hero-stat-item">
                <div className="mg-hero-stat-num">{openHoursDuration}h</div>
                <div className="mg-hero-stat-label">{t('heroDailyStatLabel', 'Daily')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── MARQUEE ── */}
      <div className="mg-marquee-section">
        <div className="mg-marquee-track">
          <div className="mg-marquee-content">
            {[...marqueeWords, ...marqueeWords, ...marqueeWords].map((item, index) => (
              <span key={`${item}-${index}`} className="mg-marquee-item">
                <span className="mg-marquee-sep" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── COURTS ── */}
      {isWebsiteSectionVisible(website, "grounds") && (
      <section id="courts" className="mg-courts-section">
        <div className="mg-container">
          <div className="mg-section-header" data-animate="fade-up">
            <p className="mg-eyebrow">{t('courtsEyebrow', 'AVAILABLE COURTS')}</p>
            <h2 className="mg-section-title">{groundCount} {t('courtsCountLabel', 'COURTS.')}<br />{sportCount} {t('sportsCountLabel', 'SPORTS.')} {t('courtsTitleSuffix', 'ONE VENUE.')}</h2>
            <p className="mg-section-sub">
              {t(
                'courtsDescription',
                'Explore all five of our premium maple hardwood courts across three sports.',
                venueGrounds.length > 0
                  ? `Explore all ${groundCount} of our premium courts across ${sportCount} sports.`
                  : '',
              )}
            </p>
          </div>

          {/* All Courts — hero + bento layout */}
          <div className="mg-courts-board">
            {/* Hero Banner — featured court */}
            {featuredCourts.map((court) => (
              <div key={court.id} className="mg-court-hero-banner">
                <img src={court.img} alt={court.name} loading="lazy" />
                <div className="mg-court-hero-banner-overlay">
                  <div className="mg-court-hero-banner-content">
                    <div className="mg-court-hero-banner-sport">
                      {court.sportIcon} {court.sportLabel}
                    </div>
                    <h3 className="mg-court-hero-banner-name">{court.name}</h3>
                    <div className="mg-court-hero-banner-footer">
                      <span className="mg-court-hero-banner-price">{court.price}</span>
                      <button className="mg-court-hero-banner-btn" onClick={() => openGroundBooking(court.ground)}>
                        {t('courtBookLabel', 'Book Now')} →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {/* Bento Grid — remaining courts */}
            <div className="mg-courts-bento">
              {bentoCourts.map((court) => (
                <div key={court.id} className="mg-court-bento-item">
                  <img src={court.img} alt={court.name} loading="lazy" />
                  <div className="mg-court-bento-overlay">
                    <span className="mg-court-bento-sport">{court.sportIcon} {court.sportLabel}</span>
                    <div className="mg-court-bento-name">{court.name}</div>
                    <div className="mg-court-bento-footer">
                      <span className="mg-court-bento-price">{court.price}</span>
                      <button className="mg-court-bento-btn" onClick={() => openGroundBooking(court.ground)}>
                        {t('courtBookLabel', 'Book Now')} →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── STATS ── */}
      {isWebsiteSectionVisible(website, "stats") && (
      <section className="mg-stats-section" ref={statsRef}>
        <div className="mg-container">
          <div className="mg-stats-grid">
            {stats.map((stat, index) => (
              <StatCell key={`${stat.label}-${index}`} stat={stat} inView={statsInView} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── ABOUT ── */}
      {isWebsiteSectionVisible(website, "about") && (
      <section id="about" className="mg-about-section">
        <div className="mg-container">
          <div className="mg-about-grid">
            <div className="mg-about-img-stack" data-animate="fade-right">
              <div className="mg-about-img-main">
                <img src={resolvedAboutImage} alt={`Players at ${venueName}`} />
              </div>
              <div className="mg-about-img-badge">
                <div className="mg-about-img-badge-num">{t('aboutBadgeNumber', '#1')}</div>
                <div className="mg-about-img-badge-text">{t('aboutBadgeText', 'In Lazimpat', heroBadgeLocation ? `In ${heroBadgeLocation}` : '')}</div>
              </div>
            </div>

            <div data-animate="fade-up">
              <p className="mg-eyebrow">{t('aboutEyebrow', 'ABOUT THE VENUE')}</p>
              <h2 className="mg-section-title">{t('aboutTitle', 'INDOOR SPORTS HALL, REDEFINED.')}</h2>
              <p className="mg-about-desc">{resolvedAboutDescription}</p>

              <div className="mg-amenities-grid">
                {amenities.map((amenity) => (
                  <div key={`${amenity.icon}-${amenity.label}`} className="mg-amenity-chip">
                    <span className="mg-amenity-icon">{amenity.icon}</span>
                    {amenity.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── TESTIMONIALS ── */}
      {isWebsiteSectionVisible(website, "testimonials") && (
      <section className="mg-testimonials-section">
        <div className="mg-container">
          <div className="mg-section-header mg-section-header-center" data-animate="fade-up">
            <p className="mg-eyebrow">{t('testimonialsEyebrow', 'PLAYER REVIEWS')}</p>
            <h2 className="mg-section-title">{t('testimonialsTitle', 'WHAT OUR COMMUNITY SAYS.')}</h2>
            <p className="mg-section-sub">{t('testimonialsDescription', 'Trusted by players, coaches, and sports teams across Kathmandu.', cleanText(venue?.location?.city) ? `Trusted by players, coaches, and sports teams across ${cleanText(venue.location.city)}.` : '')}</p>
          </div>
          <div className="mg-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={`${testimonial.name}-${index}`} className="mg-testimonial-card" data-animate="fade-up" style={{ '--delay': `${index * 0.1}s` }}>
                <div className="mg-stars">{Array.from({ length: testimonial.rating }).map((_, starIndex) => <span key={starIndex} className="mg-star">★</span>)}</div>
                <p className="mg-testimonial-text">&ldquo;{testimonial.text}&rdquo;</p>
                <div className="mg-testimonial-author">
                  <div className="mg-author-avatar">{testimonial.name.charAt(0)}</div>
                  <div>
                    <div className="mg-author-name">{testimonial.name}</div>
                    <div className="mg-author-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── FAQ ── */}
      {isWebsiteSectionVisible(website, "faqs") && (
      <section id="faq" className="mg-faq-section">
        <div className="mg-container">
          <div className="mg-faq-grid">
            <div className="mg-faq-sticky" data-animate="fade-right">
              <p className="mg-eyebrow">{t('faqEyebrow', 'FAQ')}</p>
              <h2 className="mg-section-title">{t('faqTitle', 'QUESTIONS ANSWERED.')}</h2>
              <p className="mg-faq-sticky-desc">
                {t(
                  'faqDescription',
                  'Everything you need to know before booking your first session at Merlion Gymnasium.',
                  `Everything you need to know before booking your first session at ${venueName}.`,
                )}
              </p>
            </div>
            <div className="mg-faq-list" data-animate="fade-up">
              {faqs.map((faq, index) => (
                <div key={`${faq.q}-${index}`} className={`mg-faq-item ${openFaq === index ? 'open' : ''}`}>
                  <button className="mg-faq-trigger" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                    {faq.q}
                    <span className="mg-faq-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  <div className="mg-faq-body"><p>{faq.a}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── CONTACT ── */}
      {isWebsiteSectionVisible(website, "contact") && (
      <section id="contact" className="mg-contact-section">
        <div className="mg-container">
          <div className="mg-section-header mg-section-header-center" data-animate="fade-up">
            <p className="mg-eyebrow">{t('contactEyebrow', 'GET IN TOUCH')}</p>
            <h2 className="mg-section-title">{t('contactTitle', 'READY TO PLAY?')}</h2>
            <p className="mg-section-sub">{t('contactDescription', 'Reach out to book a court, schedule coaching, or inquire about event hire.')}</p>
          </div>
          <div className="mg-contact-wrapper" data-animate="fade-up" style={{ '--delay': '0.1s' }}>
            <div className="mg-contact-grid">
              <div className="mg-contact-left">
                <div>
                  <h3 className="mg-contact-left-title">{t('contactPanelTitle', "LET'S GET ON THE COURT.")}</h3>
                  <p className="mg-contact-left-sub">{t('contactPanelDescription', 'Drop us a message or reach out directly. We respond fast.')}</p>
                </div>
                <div className="mg-contact-info-items">
                  {[
                    { icon: '📍', label: t('locationLabel', 'Location'), val: venueAddress },
                    { icon: '📞', label: t('phoneLabel', 'Phone'), val: venuePhone },
                    { icon: '🕐', label: t('hoursLabel', 'Hours'), val: `${displayHours} ${t('dailyLabel', 'Daily')}` },
                  ].map((item) => (
                    <div key={item.label} className="mg-contact-info-item">
                      <div className="mg-contact-info-icon">{item.icon}</div>
                      <div>
                        <div className="mg-contact-info-label">{item.label}</div>
                        <div className="mg-contact-info-val">{item.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mg-contact-right">
                <h4 className="mg-form-title">{t('formTitle', 'Send a Message')}</h4>
                <p className="mg-form-sub">{t('formDescription', "We'll confirm your booking within the hour.")}</p>
                <form className="mg-form" onSubmit={handleContactSubmit}>
                  <PublicContactHoneypot value={contactForm.company} onChange={handleContactChange} />
                  <div className="mg-form-row">
                    <div className="mg-input-group">
                      <label className="mg-label">{t('formNameLabel', 'Full Name')}</label>
                      <input className="mg-input" name="name" type="text" autoComplete="name" maxLength={100} placeholder={t('formNamePlaceholder', 'Your name')} value={contactForm.name} onChange={handleContactChange} required />
                    </div>
                    <div className="mg-input-group">
                      <label className="mg-label">{t('formPhoneLabel', 'Phone')}</label>
                      <PublicContactPhoneField
                        inputClassName="mg-input"
                        value={contactForm.phone}
                        countryCode={contactForm.phoneCountryCode}
                        onChange={handleContactChange}
                        required
                        placeholder={t(
                          'formPhonePlaceholder',
                          '+977',
                          cleanText(venue?.contact?.dialCode)
                            ? cleanText(venue.contact.dialCode).startsWith('+')
                              ? cleanText(venue.contact.dialCode)
                              : `+${cleanText(venue.contact.dialCode)}`
                            : '',
                        )}
                      />
                    </div>
                  </div>
                  <div className="mg-input-group">
                    <label className="mg-label">{t('formEmailLabel', 'Email')}</label>
                    <input
                      className="mg-input"
                      name="email"
                      type="email"
                      autoComplete="email"
                      maxLength={254}
                      placeholder={t('formEmailPlaceholder', 'you@email.com')}
                      value={contactForm.email}
                      onChange={handleContactChange}
                    />
                  </div>
                  <div className="mg-input-group">
                    <label className="mg-label">{t('formMessageLabel', 'Message')}</label>
                    <textarea className="mg-input" name="message" maxLength={1200} placeholder={t('formMessagePlaceholder', 'Preferred date, time, or any other info...')} value={contactForm.message} onChange={handleContactChange} required />
                  </div>
                  <button type="submit" className="mg-btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={contactSubmitting}>
                    {contactSubmitting ? 'Sending…' : t('formSubmitLabel', 'Send Message')}
                  </button>
                  <PublicContactEnquiryStatus submitting={contactSubmitting} feedback={contactFeedback} comfortableSpacing />
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── MAP ── */}
      {isWebsiteSectionVisible(website, "location") && (
      <section className="mg-map-section">
        <div className="mg-container">
          <div className="mg-map-wrap" data-animate="fade-up" style={{ position: 'relative' }}>
            <iframe
              title={`${venueName} Location — ${venueAddress}`}
              src={mapEmbedUrl}
              allowFullScreen
              loading="lazy"
            />
            <OpenMapsLink
              url={venue?.googleMapsUrl}
              latitude={venue?.location?.latitude}
              longitude={venue?.location?.longitude}
              venueName={venueName}
              label={t('openMapsLabel', 'Open in Maps')}
            />
          </div>
        </div>
      </section>
      )}

      {/* ── APP CTA ── */}
      {isWebsiteSectionVisible(website, "app") && (
      <section className="mg-cta-section">
        <div className="mg-container mg-cta-grid">
          <div data-animate="fade-right">
            <p className="mg-eyebrow mg-cta-eyebrow">{t('appEyebrow', 'DOWNLOAD THE APP')}</p>
            <h2 className="mg-section-title mg-cta-title">{t('appTitleLineOne', 'BOOK COURTS')}<br />{t('appTitleLineTwo', 'ON YOUR PHONE.')}</h2>
            <p className="mg-section-sub mg-cta-sub">
              {t(
                'appDescription',
                'Reserve your court at Merlion Gymnasium instantly through the ONGROUND app. Check real-time availability, manage bookings, and connect with other players.',
                `Reserve your court at ${venueName} instantly through the ONGROUND app. Check real-time availability, manage bookings, and connect with other players.`,
              )}
            </p>
            <div className="mg-cta-stores">
              <a href={appStoreUrl} className="mg-cta-store-btn">
                <OngroundStoreIcon
                  platform="appStore"
                  className="mg-store-icon"
                />
                <div>
                  <span className="mg-store-sub">{t('appStoreSmallLabel', 'Download on the')}</span>
                  <span className="mg-store-name">{t('appStoreMainLabel', 'App Store')}</span>
                </div>
              </a>
              <a href={googlePlayUrl} className="mg-cta-store-btn">
                <OngroundStoreIcon
                  platform="googlePlay"
                  className="mg-store-icon"
                />
                <div>
                  <span className="mg-store-sub">{t('googlePlaySmallLabel', 'Get it on')}</span>
                  <span className="mg-store-name">{t('googlePlayMainLabel', 'Google Play')}</span>
                </div>
              </a>
            </div>
          </div>
          <div className="mg-cta-mockup" data-animate="fade-left">
            <img src={resolvedAppImage} alt="ONGROUND App" className="mg-cta-img" />
          </div>
        </div>
      </section>
      )}

      {/* ── FOOTER ── */}
      <footer className="mg-footer">
        <div className="mg-container">
          <div className="mg-footer-grid">
            <div>
              <button className="mg-logo" onClick={() => scrollTo('hero')}>
                {resolvedVenueLogo ? (
                  <img
                    className="mg-logo-image"
                    src={resolvedVenueLogo}
                    alt={`${venueName} logo`}
                  />
                ) : (
                  <div className="mg-logo-icon">{brand.primary.charAt(0).toUpperCase()}</div>
                )}
                <div>
                  <div className="mg-logo-name"><span>{brand.primary}</span>{brand.secondary ? ` ${brand.secondary}` : ''}</div>
                  <span className="mg-logo-sub">{shortLocation}</span>
                </div>
              </button>
              <p className="mg-footer-tagline">{resolvedFooterTagline}</p>
            </div>
            <div>
              <div className="mg-footer-col-title">{t('footerQuickLinksTitle', 'Quick Links')}</div>
              <ul className="mg-footer-links">
                {navigation.map((link) => (
                  <li key={link.id}><button className="mg-footer-link" onClick={() => scrollTo(link.id)}>{link.label}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="mg-footer-col-title">{t('footerContactTitle', 'Contact')}</div>
              <ul className="mg-footer-links">
                <li><span className="mg-footer-link" style={{ cursor: 'default' }}>{venueAddress}</span></li>
                <li><span className="mg-footer-link" style={{ cursor: 'default' }}>{venuePhone}</span></li>
                <li><span className="mg-footer-link" style={{ cursor: 'default' }}>{displayHours}</span></li>
              </ul>
            </div>
            {footerSocialItems.length > 0 && (
              <div>
              <div className="mg-footer-col-title">{t('footerSocialTitle', 'Follow Us')}</div>
              <ul className="mg-footer-links">
                {footerSocialItems.map((item) => (
                  <li key={item.key}>
                    <a
                      href={item.href}
                      className="mg-footer-link"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.icon} {item.label}
                    </a>
                  </li>
                ))}
              </ul>
              </div>
            )}
          </div>
          <div className="mg-footer-bottom">
            <p className="mg-footer-copy">&copy; {new Date().getFullYear()} {venueName}{t('footerCopyrightSuffix', '. All rights reserved.')}</p>
            <a
              className="mg-footer-powered"
              href="https://getonground.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit Onground website"
            >
              {t('footerPoweredByLabel', 'Powered by')} <img src={resolvedPoweredLogo} alt={t('poweredLogoAlt', 'ONGROUND')} style={{ height: '20px' }} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Stat Cell Sub-component ──────────────────────────────────────────────────
function StatCell({ stat, inView, delay }) {
  const count = useCounter(stat.n, inView);

  return (
    <div className="mg-stat-item" data-animate="fade-up" style={{ '--delay': `${delay}s` }}>
      <div className="mg-stat-num">
        {count.toLocaleString()}<span className="mg-stat-suffix">{stat.suffix}</span>
      </div>
      <div className="mg-stat-label">{stat.label}</div>
    </div>
  );
}
